import PropTypes from 'prop-types';
import React, { createContext, useContext, useState, useCallback } from 'react';

const ChatPageContext = createContext();

/**
 * Provider that holds the current page's contextual state for the chat widget.
 * Each page updates this via useChatPageEffect so the widget can pass rich
 * context to the AI agent with every message.
 *
 * Also exposes openChat(message) to allow any component to open the floating
 * chat widget with a pre-filled message (e.g., "Ask Bili about this dataset").
 */
export function ChatPageProvider({ children }) {
    const [pageState, setPageState] = useState({});
    const [chatRequest, setChatRequest] = useState(null);

    const openChat = useCallback((message) => {
        setChatRequest({ message, id: Date.now() });
    }, []);

    const clearChatRequest = useCallback(() => {
        setChatRequest(null);
    }, []);

    return (
        <ChatPageContext.Provider
            value={{
                pageState,
                setPageState,
                chatRequest,
                openChat,
                clearChatRequest,
            }}
        >
            {children}
        </ChatPageContext.Provider>
    );
}

ChatPageProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

/**
 * Hook to access the current chat page context.
 * - Pages call setPageState() to publish their state.
 * - Components call openChat(message) to open the widget with a prefilled message.
 * - The chat widget reads pageState to include in messages.
 */
export function useChatPageState() {
    return useContext(ChatPageContext);
}
